import { type FC, useId } from 'react'
import { FormControl } from '@mui/material'
import { useField } from 'formik'

import { useTranslation } from 'react-i18next'
import { Body1, RadioGroup, Stack, useIsMobileVariant } from '@dhl-ui/components'
import { FlexibleListItem, FlexibleListItemContainer, type FlexibleListItemRadioProps } from '@dhl/receiver-design'
import { AgreedPlaceIllustration, NeighbourIllustration } from '@dhl-ui/illustrations'

import { type FallbackType } from '@dhl/receiver-preference-api'

type Props = Omit<React.HTMLProps<HTMLInputElement>, 'label' | 'onBlur' | 'onChange' | 'value'> & {
  onChange?: (preference: FallbackType) => void
  value?: FallbackType
}

const NeighboursOption: FC<FlexibleListItemRadioProps> = (props) => {
  const { t } = useTranslation()

  return (
    <FlexibleListItem
      data-testid="not-home-neighbours-choice"
      illustration={<NeighbourIllustration />}
      title={t('portal.profile.preferences.options.neighbours.title')}
      {...props}
    >
      {t('portal.profile.preferences.options.DoorDelivery.fallbackDescriptions.NoPreference')}
    </FlexibleListItem>
  )
}

const AgreedPlaceOption: FC<FlexibleListItemRadioProps> = (props) => {
  const { t } = useTranslation()

  return (
    <FlexibleListItem
      data-testid="not-home-agreed-place-choice"
      illustration={<AgreedPlaceIllustration />}
      title={t('portal.profile.preferences.options.AgreedPlaceDelivery.title')}
      {...props}
    >
      {t('portal.profile.preferences.options.DoorDelivery.fallbackDescriptions.AgreedPlaceDelivery')}
    </FlexibleListItem>
  )
}

export const NotHomeFallbackSelect: FC<Props> = ({ name, onChange, value }) => {
  const { t } = useTranslation()
  const labelId = useId()
  const isMobileVariant = useIsMobileVariant()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedPreference = event.target.value as FallbackType
    onChange?.(selectedPreference)
  }

  return (
    <FormControl fullWidth>
      <Stack spacing={2}>
        {/* Empty string keeps RadioGroup controlled while the preference is still loading */}
        <RadioGroup aria-labelledby={labelId} name={name} value={value ?? ''} onChange={handleChange}>
          <FlexibleListItemContainer>
            <NeighboursOption value="NoPreference" />
            <AgreedPlaceOption value="AgreedPlaceDelivery" />
          </FlexibleListItemContainer>
        </RadioGroup>

        {value && isMobileVariant && (
          <Body1>{t(`portal.profile.preferences.options.DoorDelivery.fallbackDescriptions.${value}`)}</Body1>
        )}
      </Stack>
    </FormControl>
  )
}

type NotHomeFallbackSelectInputProps = {
  name: string
}

export const NotHomeFallbackSelectInput: FC<NotHomeFallbackSelectInputProps> = ({ name }) => {
  const [field, , helpers] = useField<FallbackType>(name)

  const handleChange = (preference: FallbackType) => {
    helpers.setValue(preference)
  }

  return <NotHomeFallbackSelect name={name} value={field.value} onChange={handleChange} />
}
